import { withoutQueryString } from '../../common/utils';
import { clearCredentials } from '../auth';

/**
 * It is the responsibility of the data fetchers to also convert the data to
 * the type used in the UI.
 */

export interface ServerError {
  status: number;
  title: string;
  detail?: string;
}
export class RemoteDataError extends Error {
  constructor(
    message: string,
    public readonly uri: string,
    public readonly status: number,
    public readonly statusText: string,
    public readonly responseContent?: ServerError,
  ) {
    super(message);
  }
}

const MAX_QUERY_RETRIES = 3;

// Client errors won't succeed on retry
export function shouldRetryQuery(failureCount: number, error: unknown) {
  const isClientError =
    error instanceof RemoteDataError &&
    error.status >= 400 &&
    error.status < 500;
  return failureCount < MAX_QUERY_RETRIES && !isClientError;
}

declare module '@tanstack/react-query' {
  interface Register {
    defaultError: RemoteDataError;
  }
}

type HTTPMethod = 'GET' | 'PUT' | 'DELETE' | 'POST' | 'PATCH';

export async function doFetch<
  ApiResponseType,
  ReturnType = ApiResponseType,
  BodyPayload = undefined,
>({
  method,
  uri,
  payload,
  converter,
  customHeaders,
}: {
  method: HTTPMethod;
  uri: string;
  converter: (data: ApiResponseType) => ReturnType;
  customHeaders?: Array<[name: string, value: string]>;
  // It would be preferable to make this not optional so that the compiler tells
  // us if we forget to attach the payload to the call, but then we have to
  // explicitly call this with `payload: undefined` if we don't have a payload.
  // :sad_trombone:
  payload?: BodyPayload;
}): Promise<ReturnType> {
  const headers = new Headers();
  headers.append('Accept', 'application/json');
  // TODO: figure out why we have this
  headers.append('X-Requested-With', 'XmlHttpRequest');

  if (payload) {
    headers.append('Content-Type', 'application/json');
  }
  customHeaders?.forEach(([name, value]) => headers.append(name, value));

  const response = await fetch(uri, {
    method,
    headers,
    body: payload && JSON.stringify(payload),
  });
  // Query strings can hold tokens, which must not end up in error reports
  const uriPath = withoutQueryString(uri);

  if (response.status === 401) {
    // Unauthorized. Not logged in or cookie expired.
    clearCredentials();

    // Redirect to login page
    window.location.assign(
      `/user/sign-in?next=${encodeURIComponent(
        window.location.pathname + window.location.search,
      )}`,
    );
    throw new RemoteDataError(
      'User not allowed to access resource. Logging out.',
      uriPath,
      response.status,
      response.statusText,
    );
  }

  if (!response.ok) {
    let responseContent;
    try {
      responseContent = await response.json();
    } catch {
      // Ignored, no content
    }
    throw new RemoteDataError(
      `Error while fetching ${uriPath}: ${response.status} ${response.statusText}`,
      uriPath,
      response.status,
      response.statusText,
      responseContent,
    );
  }

  const data: ApiResponseType =
    // 204 = No Response. ApiResponseType should expect {}
    response.status === 204 ? {} : await response.json();
  return converter(data);
}
